import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { 
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  AreaChart, Area, BarChart, Bar, ScatterChart, Scatter, Cell
} from 'recharts';
import { 
  Zap, TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  Calendar, DollarSign, RefreshCw, Target, Activity, 
  ThermometerSun, Clock, MapPin, Eye, Search
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ANOMALY_COLORS = {
  HIGH: '#EF4444',
  MEDIUM: '#F59E0B',
  LOW: '#3B82F6'
};

const STATUS_CONFIG = {
  NEW: { color: 'bg-red-100 text-red-700', label: 'New' },
  INVESTIGATING: { color: 'bg-yellow-100 text-yellow-700', label: 'Investigating' },
  EXPLAINED: { color: 'bg-blue-100 text-blue-700', label: 'Explained' },
  RESOLVED: { color: 'bg-green-100 text-green-700', label: 'Resolved' }
};

const BUDGET_STATUS = {
  ON_TRACK: { color: 'bg-green-100 text-green-700', label: 'On Track' },
  OVER: { color: 'bg-red-100 text-red-700', label: 'Over Budget' },
  UNDER: { color: 'bg-blue-100 text-blue-700', label: 'Under Budget' }
};

export default function ForecastingDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [forecastHorizon, setForecastHorizon] = useState('30d');
  const [selectedAnomaly, setSelectedAnomaly] = useState(null);
  const [showResolveModal, setShowResolveModal] = useState(false);

  // Fetch dashboard summary
  const { data: summaryData, isLoading } = useQuery({
    queryKey: ['forecasting-summary'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/forecasting/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch consumption history
  const { data: historyData } = useQuery({
    queryKey: ['consumption-history'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/forecasting/consumption/history?days=60`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch forecast
  const { data: forecastData } = useQuery({
    queryKey: ['forecast', forecastHorizon],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/forecasting/forecast?horizon=${forecastHorizon}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch heatmap
  const { data: heatmapData } = useQuery({
    queryKey: ['consumption-heatmap'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/forecasting/consumption/heatmap`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch anomalies
  const { data: anomaliesData, refetch: refetchAnomalies } = useQuery({
    queryKey: ['anomalies'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/forecasting/anomalies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch budget
  const { data: budgetData } = useQuery({
    queryKey: ['budget'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/forecasting/budget`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch weather impact
  const { data: weatherData } = useQuery({
    queryKey: ['weather-impact'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/forecasting/weather-impact`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch location summary
  const { data: locationsData } = useQuery({
    queryKey: ['forecast-locations'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/forecasting/locations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  const summary = summaryData?.summary || {};
  const modelInfo = summaryData?.model_info || {};
  const history = historyData?.data || [];
  const forecast = forecastData?.forecast || [];
  const heatmap = heatmapData?.heatmap || [];
  const anomalies = anomaliesData?.anomalies || [];
  const anomalySummary = anomaliesData?.summary || {};
  const budget = budgetData?.budget || {};
  const weather = weatherData?.data_points || [];
  const locations = locationsData?.locations || [];

  // Prepare chart data - combine history and forecast
  const combinedData = [
    ...history.slice(-30).map(h => ({
      date: h.date,
      actual: h.consumption_kwh,
      forecast: null,
      lower: null,
      upper: null
    })),
    ...forecast.slice(0, 14).map(f => ({
      date: f.date,
      actual: null,
      forecast: f.predicted_kwh,
      lower: f.lower_bound_kwh,
      upper: f.upper_bound_kwh
    }))
  ];

  // Budget chart data
  const budgetChartData = (budget.months || []).map(m => ({
    month: m.month_name?.substring(0, 3),
    budget: m.budget,
    actual: m.actual,
    isProjected: m.is_projected
  }));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="loading-state">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Create heatmap matrix for visualization
  const heatmapMatrix = [];
  for (let hour = 0; hour < 24; hour++) {
    const row = { hour: `${hour}:00` };
    ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].forEach(day => {
      const cell = heatmap.find(h => h.hour === hour && h.day === day);
      row[day] = cell?.consumption_kwh || 0;
    });
    heatmapMatrix.push(row);
  }

  return (
    <div className="p-8 space-y-6" data-testid="forecasting-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <span className="text-gradient-primary">Load Forecasting & Analytics</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            Predict consumption, detect anomalies, and track energy budgets
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            Model: {modelInfo.model_version || 'v2.3.1'}
          </Badge>
          <Badge className="bg-green-100 text-green-700">
            MAPE: {modelInfo.accuracy_mape || 4.2}%
          </Badge>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-2 hover:shadow-lg transition-shadow" data-testid="kpi-consumption">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-muted-foreground">Avg Daily</p>
              <Zap className="w-5 h-5 text-yellow-500" />
            </div>
            <p className="text-3xl font-bold">{summary.avg_daily_consumption_kwh?.toLocaleString() || 0}</p>
            <p className="text-xs text-muted-foreground">kWh</p>
          </CardContent>
        </Card>

        <Card className="border-2 hover:shadow-lg transition-shadow" data-testid="kpi-forecast">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-muted-foreground">Next 7 Days</p>
              <Calendar className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold">{summary.next_week_forecast_kwh?.toLocaleString() || 0}</p>
            <p className="text-xs text-muted-foreground">kWh forecast</p>
          </CardContent>
        </Card>

        <Card className="border-2 hover:shadow-lg transition-shadow" data-testid="kpi-confidence">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-muted-foreground">Confidence</p>
              <Target className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold">{summary.forecast_confidence || 0}%</p>
            <p className="text-xs text-muted-foreground">Model accuracy</p>
          </CardContent>
        </Card>

        <Card className={`border-2 hover:shadow-lg transition-shadow ${summary.active_anomalies > 0 ? 'border-orange-200' : ''}`} data-testid="kpi-anomalies">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-muted-foreground">Anomalies</p>
              <AlertTriangle className={`w-5 h-5 ${summary.active_anomalies > 0 ? 'text-orange-500' : 'text-gray-400'}`} />
            </div>
            <p className={`text-3xl font-bold ${summary.active_anomalies > 0 ? 'text-orange-600' : ''}`}>
              {summary.active_anomalies || 0}
            </p>
            <p className="text-xs text-muted-foreground">Active alerts</p>
          </CardContent>
        </Card>

        <Card className={`border-2 hover:shadow-lg transition-shadow`} data-testid="kpi-budget">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-muted-foreground">Budget</p>
              <DollarSign className="w-5 h-5 text-green-500" />
            </div>
            <Badge className={BUDGET_STATUS[summary.budget_status]?.color || 'bg-gray-100'}>
              {BUDGET_STATUS[summary.budget_status]?.label || 'Unknown'}
            </Badge>
            <p className="text-sm mt-2">{summary.budget_variance_pct > 0 ? '+' : ''}{summary.budget_variance_pct}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="forecast">Forecast</TabsTrigger>
          <TabsTrigger value="anomalies">Anomalies ({anomalySummary.new || 0})</TabsTrigger>
          <TabsTrigger value="budget">Budget</TabsTrigger>
          <TabsTrigger value="weather">Weather Impact</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Main Forecast Chart */}
          <Card data-testid="forecast-chart">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Consumption & Forecast</CardTitle>
                  <CardDescription>Historical actuals with forward forecast</CardDescription>
                </div>
                <Select value={forecastHorizon} onValueChange={setForecastHorizon}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">7 Days</SelectItem>
                    <SelectItem value="30d">30 Days</SelectItem>
                    <SelectItem value="12m">12 Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={combinedData}>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="actual" 
                    stroke="#1F2937" 
                    fill="#E5E7EB" 
                    name="Actual" 
                    strokeWidth={2}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="forecast" 
                    stroke="#3B82F6" 
                    fill="#DBEAFE" 
                    name="Forecast"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="upper" 
                    stroke="transparent" 
                    fill="#3B82F6" 
                    fillOpacity={0.1}
                    name="Upper Bound"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="lower" 
                    stroke="transparent" 
                    fill="#FFFFFF" 
                    name="Lower Bound"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Consumption Heatmap */}
            <Card data-testid="consumption-heatmap">
              <CardHeader>
                <CardTitle className="text-lg">Usage Pattern Heatmap</CardTitle>
                <CardDescription>Hour of day vs day of week</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <div className="flex gap-1">
                    <div className="w-12"></div>
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                      <div key={day} className="w-10 text-center text-xs font-medium">{day}</div>
                    ))}
                  </div>
                  {[0, 4, 8, 12, 16, 20].map(hour => (
                    <div key={hour} className="flex gap-1 mt-1">
                      <div className="w-12 text-xs text-muted-foreground">{hour}:00</div>
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => {
                        const cell = heatmap.find(h => h.hour === hour && h.day === day);
                        const value = cell?.consumption_kwh || 0;
                        const maxValue = 700;
                        const intensity = Math.min(value / maxValue, 1);
                        return (
                          <div 
                            key={day} 
                            className="w-10 h-6 rounded"
                            style={{ 
                              backgroundColor: `rgba(59, 130, 246, ${intensity})`,
                            }}
                            title={`${day} ${hour}:00 - ${value} kWh`}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-center gap-2 mt-4 text-xs text-muted-foreground">
                  <span>Low</span>
                  <div className="flex gap-0.5">
                    {[0.1, 0.3, 0.5, 0.7, 0.9].map(i => (
                      <div 
                        key={i} 
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: `rgba(59, 130, 246, ${i})` }}
                      />
                    ))}
                  </div>
                  <span>High</span>
                </div>
              </CardContent>
            </Card>

            {/* Anomaly Feed */}
            <Card data-testid="anomaly-feed">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Recent Anomalies</CardTitle>
                    <CardDescription>Unusual consumption patterns</CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('anomalies')}>
                    View All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {anomalies.slice(0, 3).map((anomaly) => (
                    <div 
                      key={anomaly.id}
                      className={`p-3 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-shadow ${
                        anomaly.severity === 'HIGH' ? 'border-l-red-500 bg-red-50' :
                        anomaly.severity === 'MEDIUM' ? 'border-l-yellow-500 bg-yellow-50' :
                        'border-l-blue-500 bg-blue-50'
                      }`}
                      onClick={() => setSelectedAnomaly(anomaly)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className={`w-4 h-4 ${
                            anomaly.severity === 'HIGH' ? 'text-red-500' :
                            anomaly.severity === 'MEDIUM' ? 'text-yellow-500' : 'text-blue-500'
                          }`} />
                          <span className="font-medium text-sm">{anomaly.type.replace('_', ' ')}</span>
                        </div>
                        <Badge className={STATUS_CONFIG[anomaly.status]?.color}>
                          {STATUS_CONFIG[anomaly.status]?.label}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{anomaly.location}</p>
                      <p className="text-xs mt-1">
                        Deviation: <span className={`font-medium ${anomaly.deviation_pct > 0 ? 'text-red-600' : 'text-blue-600'}`}>
                          {anomaly.deviation_pct > 0 ? '+' : ''}{anomaly.deviation_pct}%
                        </span>
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Location Performance */}
          <Card data-testid="location-performance">
            <CardHeader>
              <CardTitle className="text-lg">Forecast Accuracy by Location</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Location</th>
                      <th className="text-right py-3 px-4">YTD Actual</th>
                      <th className="text-right py-3 px-4">YTD Forecast</th>
                      <th className="text-center py-3 px-4">Error %</th>
                      <th className="text-center py-3 px-4">Trend</th>
                      <th className="text-center py-3 px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations.map((loc) => (
                      <tr key={loc.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <div>
                              <p className="font-medium">{loc.name}</p>
                              <p className="text-xs text-muted-foreground">{loc.state}</p>
                            </div>
                          </div>
                        </td>
                        <td className="text-right py-3 px-4">{loc.ytd_actual_kwh?.toLocaleString()} kWh</td>
                        <td className="text-right py-3 px-4">{loc.ytd_forecast_kwh?.toLocaleString()} kWh</td>
                        <td className="text-center py-3 px-4">{loc.error_pct}%</td>
                        <td className="text-center py-3 px-4">
                          {loc.trend === 'up' ? <TrendingUp className="w-4 h-4 text-red-500 mx-auto" /> :
                           loc.trend === 'down' ? <TrendingDown className="w-4 h-4 text-green-500 mx-auto" /> :
                           <Activity className="w-4 h-4 text-gray-400 mx-auto" />}
                        </td>
                        <td className="text-center py-3 px-4">
                          <Badge className={
                            loc.forecast_status === 'GOOD' ? 'bg-green-100 text-green-700' :
                            loc.forecast_status === 'WARNING' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }>
                            {loc.forecast_status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Forecast Tab */}
        <TabsContent value="forecast" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Detailed Forecast</CardTitle>
                <Select value={forecastHorizon} onValueChange={setForecastHorizon}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">7 Days</SelectItem>
                    <SelectItem value="30d">30 Days</SelectItem>
                    <SelectItem value="12m">12 Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={forecast}>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="upper_bound_kwh" 
                    stroke="transparent" 
                    fill="#3B82F6" 
                    fillOpacity={0.2}
                    name="Upper Bound"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="predicted_kwh" 
                    stroke="#3B82F6" 
                    fill="#3B82F6"
                    fillOpacity={0.4}
                    strokeWidth={2}
                    name="Predicted"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="lower_bound_kwh" 
                    stroke="transparent" 
                    fill="#FFFFFF"
                    name="Lower Bound"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Anomalies Tab */}
        <TabsContent value="anomalies" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-3xl font-bold">{anomalySummary.total || 0}</p>
              </CardContent>
            </Card>
            <Card className="border-red-200">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">New</p>
                <p className="text-3xl font-bold text-red-600">{anomalySummary.new || 0}</p>
              </CardContent>
            </Card>
            <Card className="border-yellow-200">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Investigating</p>
                <p className="text-3xl font-bold text-yellow-600">{anomalySummary.investigating || 0}</p>
              </CardContent>
            </Card>
            <Card className="border-green-200">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Resolved</p>
                <p className="text-3xl font-bold text-green-600">{anomalySummary.resolved || 0}</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">All Anomalies</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {anomalies.map((anomaly) => (
                  <div 
                    key={anomaly.id}
                    className={`p-4 rounded-lg border-l-4 ${
                      anomaly.severity === 'HIGH' ? 'border-l-red-500 bg-red-50' :
                      anomaly.severity === 'MEDIUM' ? 'border-l-yellow-500 bg-yellow-50' :
                      'border-l-blue-500 bg-blue-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{anomaly.type.replace('_', ' ')}</Badge>
                          <Badge className={`bg-${anomaly.severity === 'HIGH' ? 'red' : anomaly.severity === 'MEDIUM' ? 'yellow' : 'blue'}-100 text-${anomaly.severity === 'HIGH' ? 'red' : anomaly.severity === 'MEDIUM' ? 'yellow' : 'blue'}-700`}>
                            {anomaly.severity}
                          </Badge>
                          <Badge className={STATUS_CONFIG[anomaly.status]?.color}>
                            {STATUS_CONFIG[anomaly.status]?.label}
                          </Badge>
                        </div>
                        <p className="font-medium mt-2">{anomaly.location}</p>
                        <p className="text-sm text-muted-foreground">
                          Expected: {anomaly.expected_kwh?.toLocaleString()} kWh | 
                          Actual: {anomaly.actual_kwh?.toLocaleString()} kWh | 
                          Deviation: <span className={anomaly.deviation_pct > 0 ? 'text-red-600' : 'text-blue-600'}>
                            {anomaly.deviation_pct > 0 ? '+' : ''}{anomaly.deviation_pct}%
                          </span>
                        </p>
                        {anomaly.explanation && (
                          <p className="text-sm mt-2 text-green-700">
                            <CheckCircle className="w-4 h-4 inline mr-1" />
                            {anomaly.explanation}
                          </p>
                        )}
                        {anomaly.suggested_causes?.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-muted-foreground">Possible causes:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {anomaly.suggested_causes.map((cause, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{cause}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {anomaly.status === 'NEW' && (
                          <Button size="sm" variant="outline">Investigate</Button>
                        )}
                        {anomaly.status === 'INVESTIGATING' && (
                          <Button size="sm" onClick={() => { setSelectedAnomaly(anomaly); setShowResolveModal(true); }}>
                            Resolve
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Budget Tab */}
        <TabsContent value="budget" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Annual Budget</p>
                <p className="text-2xl font-bold">{formatCurrency(budget.annual_budget || 0)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">YTD Budget</p>
                <p className="text-2xl font-bold">{formatCurrency(budget.ytd_budget || 0)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">YTD Actual</p>
                <p className="text-2xl font-bold">{formatCurrency(budget.ytd_actual || 0)}</p>
              </CardContent>
            </Card>
            <Card className={budget.ytd_variance_pct > 5 ? 'border-red-200' : budget.ytd_variance_pct < -5 ? 'border-green-200' : ''}>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">YTD Variance</p>
                <p className={`text-2xl font-bold ${budget.ytd_variance_pct > 5 ? 'text-red-600' : budget.ytd_variance_pct < -5 ? 'text-green-600' : ''}`}>
                  {budget.ytd_variance_pct > 0 ? '+' : ''}{budget.ytd_variance_pct}%
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Monthly Budget vs Actual</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={budgetChartData}>
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                  <Bar dataKey="budget" fill="#E5E7EB" name="Budget" />
                  <Bar dataKey="actual" fill="#3B82F6" name="Actual" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weather Impact Tab */}
        <TabsContent value="weather" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Temperature vs Consumption</CardTitle>
              <CardDescription>Understanding weather sensitivity</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <ScatterChart>
                  <XAxis dataKey="temperature_f" name="Temperature" unit="°F" />
                  <YAxis dataKey="consumption_kwh" name="Consumption" unit=" kWh" />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                  <Scatter name="Consumption" data={weather} fill="#3B82F6" />
                </ScatterChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-3 gap-4 mt-4 text-center">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <ThermometerSun className="w-6 h-6 mx-auto text-blue-500" />
                  <p className="text-sm font-medium mt-2">Heating Threshold</p>
                  <p className="text-lg font-bold">&lt; 50°F</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="w-6 h-6 mx-auto text-green-500" />
                  <p className="text-sm font-medium mt-2">Comfort Range</p>
                  <p className="text-lg font-bold">50-70°F</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg">
                  <ThermometerSun className="w-6 h-6 mx-auto text-orange-500" />
                  <p className="text-sm font-medium mt-2">Cooling Threshold</p>
                  <p className="text-lg font-bold">&gt; 70°F</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">7-Day Weather Forecast Impact</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-2">
                {(weatherData?.upcoming_weather || []).map((day, i) => (
                  <div key={i} className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">{new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}</p>
                    <p className="text-lg font-bold">{day.high_f}°</p>
                    <p className="text-sm text-muted-foreground">{day.low_f}°</p>
                    <Badge className={
                      day.expected_impact === 'HIGH_COOLING' ? 'bg-orange-100 text-orange-700' :
                      day.expected_impact === 'HIGH_HEATING' ? 'bg-blue-100 text-blue-700' :
                      'bg-green-100 text-green-700'
                    } variant="outline">
                      {day.expected_impact === 'NORMAL' ? 'Normal' : day.expected_impact === 'HIGH_COOLING' ? 'High AC' : 'High Heat'}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Resolve Anomaly Modal */}
      <Dialog open={showResolveModal} onOpenChange={setShowResolveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Anomaly</DialogTitle>
            <DialogDescription>Provide an explanation for this anomaly</DialogDescription>
          </DialogHeader>
          {selectedAnomaly && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{selectedAnomaly.type?.replace('_', ' ')} - {selectedAnomaly.location}</p>
                <p className="text-sm text-muted-foreground">
                  Deviation: {selectedAnomaly.deviation_pct}%
                </p>
              </div>
              <div>
                <label className="text-sm font-medium">Explanation</label>
                <Textarea placeholder="What caused this anomaly?" className="mt-2" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveModal(false)}>Cancel</Button>
            <Button onClick={() => { setShowResolveModal(false); refetchAnomalies(); }}>Mark as Resolved</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
